import type { FC, PropsWithChildren } from "react";
import { useState, useEffect, useCallback } from "react";

import type { Direction } from "./styles";
import { CloseButton, SidebarContainer } from "./styles";

interface ISidebarProps {
    /** Show close button */
    showCloseButton?: boolean;
    /** Additional custom class to sidebar */
    sidebarClassName?: string;
    /** Additional custom class to close button */
    closeButtonClassName?: string;
    /** Animation style (possible variants: overlay) */
    animationType?: string;
    /** Direction the sidebar should appear on (possible variants: top, right, bottom, left) */
    animationDirection?: Direction;
    /** Controls whether the sidebar is visible on the page */
    isSidebarOpen: boolean;
    /** Callback that is called on open and on close the sidebar */
    onSidebarToggle?: (isOpen: boolean) => void;
    /** Callback that is called on click the "Close" button */
    onCloseButtonClick?: () => void;
}

export const Sidebar: FC<PropsWithChildren<ISidebarProps>> = ({
    showCloseButton = true,
    sidebarClassName,
    closeButtonClassName,
    animationType = "overlay",
    animationDirection = "right",
    isSidebarOpen,
    children,
    onSidebarToggle,
    onCloseButtonClick,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (isSidebarOpen) {
            setShouldRender(true);
            setIsOpen(true);
        } else {
            setIsOpen(false);
            const timer = setTimeout(() => setShouldRender(false), 400);
            return () => clearTimeout(timer);
        }
    }, [isSidebarOpen]);

    const toggleSidebar = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onSidebarToggle?.(open);
        },
        [onSidebarToggle],
    );

    useEffect(() => {
        if (isOpen !== isSidebarOpen) {
            toggleSidebar(isSidebarOpen);
        }
    }, [isSidebarOpen, isOpen, toggleSidebar]);

    const handleCloseButtonClick = useCallback(() => {
        toggleSidebar(false);
        onCloseButtonClick?.();
    }, [onCloseButtonClick, toggleSidebar]);

    if (!shouldRender) {
        return null;
    }

    return (
        <SidebarContainer
            className={sidebarClassName}
            $isOpen={isOpen}
            $animationType={animationType}
            $animationDirection={animationDirection}
        >
            {showCloseButton ? (
                <CloseButton
                    className={closeButtonClassName}
                    iconSize={"xs"}
                    iconName={"close"}
                    type={"flat"}
                    onClick={handleCloseButtonClick}
                />
            ) : null}
            {children}
        </SidebarContainer>
    );
};
